import { ComponentType, lazy } from 'react';

const REFRESH_STORAGE_KEY = 'retry-lazy-refreshed';
let reloadScheduled = false;

const retry = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): Promise<{ default: T }> => {
  return new Promise((resolve, reject) => {
    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem(REFRESH_STORAGE_KEY) || 'false'
    );

    // try to import the component
    factory()
      .then((component) => {
        window.sessionStorage.setItem(REFRESH_STORAGE_KEY, 'false'); // success so reset the refresh
        resolve(component);
      })
      .catch((error: unknown) => {
        if (!hasRefreshed && !reloadScheduled) {
          // not been refreshed yet — schedule a reload
          reloadScheduled = true;
          window.sessionStorage.setItem(REFRESH_STORAGE_KEY, 'true');
          window.location.reload();
        } else if (hasRefreshed && !reloadScheduled) {
          reject(error); // already reloaded once, surface the error
        }
        // else: reload in flight — swallow, page is unloading
      });
  });
};

export const lazyRetry = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): ReturnType<typeof lazy<T>> => {
  if (import.meta.env.DEV) {
    return lazy(factory);
  }

  return lazy(() => retry(factory));
};
